import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import ReportCard from '@/components/base/ReportCard';
import { reports } from '@/mocks/reports';

export default function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const report = reports.find((r) => r.id === id);

  const related = useMemo(() => {
    if (!report) return [];
    return reports.filter((r) => r.id !== report.id && r.category === report.category).slice(0, 3);
  }, [report]);

  if (!report) return <Navigate to="/reports" replace />;

  const paragraphs = report.content.split('\n\n').filter(Boolean);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: report.title,
    description: report.summary,
    datePublished: report.date,
    author: { '@id': 'https://xodxd.cn/#org' },
    publisher: { '@id': 'https://xodxd.cn/#org' },
    image: report.imageUrl,
    url: `https://xodxd.cn/reports/${report.id}`,
    keywords: report.tags.join(', '),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xodxd.cn/' },
      { '@type': 'ListItem', position: 2, name: '已核实报道', item: 'https://xodxd.cn/reports' },
      { '@type': 'ListItem', position: 3, name: report.title, item: `https://xodxd.cn/reports/${report.id}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-16">
        {/* Cover */}
        <div className="relative w-full h-64 md:h-96 overflow-hidden">
          <img
            src={report.imageUrl}
            alt={report.imageAlt}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-transparent" />
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Article */}
          <article className="lg:col-span-2">
            {/* Breadcrumb */}
            <nav aria-label="面包屑" className="text-xs text-foreground-400 mb-5">
              <ol className="flex items-center gap-1.5 flex-wrap">
                <li><Link to="/" className="hover:text-foreground-700 transition-colors">首页</Link></li>
                <li><i className="ri-arrow-right-s-line" /></li>
                <li><Link to="/reports" className="hover:text-foreground-700 transition-colors">已核实报道</Link></li>
                <li><i className="ri-arrow-right-s-line" /></li>
                <li><Link to={`/reports?cat=${report.category}`} className="hover:text-foreground-700 transition-colors">{report.categoryLabel}</Link></li>
              </ol>
            </nav>

            {/* Meta */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="tag-badge">{report.categoryLabel}</span>
              {report.isHot && <span className="tag-badge dark">热门</span>}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight">
              {report.title}
            </h1>

            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-6 pb-6 border-b border-background-200">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" /> {report.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" /> 阅读约{report.readTime}分钟
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-shield-check-line text-secondary-600" />
                <span className="text-secondary-700 font-medium">已核实</span>
              </span>
            </div>

            {/* Summary */}
            <div className="bg-background-100 border-l-4 border-primary-500 pl-4 py-3 pr-4 rounded-r-lg mb-8 text-sm text-foreground-700 leading-relaxed">
              <strong className="block text-xs uppercase tracking-wider text-foreground-500 mb-1">摘要</strong>
              {report.summary}
            </div>

            {/* Content */}
            <div className="prose-custom">
              {paragraphs.map((para, i) => (
                <p key={i} className="text-foreground-800 text-sm md:text-base leading-loose mb-5">
                  {para}
                </p>
              ))}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
              {report.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/reports?q=${encodeURIComponent(tag)}`}
                  className="text-xs px-2.5 py-1 bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-700 rounded transition-colors whitespace-nowrap"
                >
                  # {tag}
                </Link>
              ))}
            </div>

            {/* Disclaimer */}
            <div className="mt-8 bg-secondary-50 border border-secondary-200 rounded-lg p-4 text-xs text-secondary-800 leading-relaxed">
              <strong className="block mb-1">17爆料核实声明</strong>
              本报道基于多方核查信息发布，编辑部对关键事实进行了独立核实。如有异议或补充信息，欢迎通过17爆料私信渠道联系我们。
            </div>

            {/* CTA */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/submit"
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-md transition-colors whitespace-nowrap"
              >
                <i className="ri-send-plane-line" /> 我也有料，立即投稿
              </Link>
              <Link
                to="/reports"
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-semibold text-sm rounded-md transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" /> 返回报道列表
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="flex flex-col gap-6">
            {/* Related */}
            {related.length > 0 && (
              <div>
                <h2 className="headline-divider font-heading text-base font-bold text-foreground-950 mb-4">同栏目报道</h2>
                <div className="flex flex-col">
                  {related.map((r) => (
                    <ReportCard key={r.id} report={r} variant="compact" />
                  ))}
                </div>
              </div>
            )}

            {/* Submit box */}
            <div className="bg-foreground-950 rounded-lg p-5 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-400 mx-auto mb-3">
                <i className="ri-lightbulb-flash-line text-xl" />
              </div>
              <h3 className="font-heading font-semibold text-background-50 text-sm mb-2">你有相关线索？</h3>
              <p className="text-xs text-background-400 leading-relaxed mb-4">
                通过17爆料投稿或私信提供线索，核实后我们将跟进发布。
              </p>
              <Link
                to="/submit"
                className="block w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-xs rounded-md transition-colors whitespace-nowrap"
              >
                立即投稿
              </Link>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}